<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import ReportsAPI from 'dashboard/api/reports';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import TeamTransferMatrix from './TeamTransferMatrix.vue';
import SummaryTeamOperatorsTable from './SummaryTeamOperatorsTable.vue';
import { formatTime } from '../constants';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import {
  buildConversationArchiveFilters,
  buildDailyArchiveFilters,
} from '../helpers/summaryReportArchiveHelper';
import {
  formatDynamicsBucketLabel,
  resolveDynamicsGranularity,
} from '../helpers/dynamicsGranularityHelper';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value => ['agent', 'team', 'label', 'inbox'].includes(value),
  },
  entityId: {
    type: [String, Number],
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const allLabels = useMapGetter('labels/getLabels');
const { openArchive } = useSummaryReportArchive();

const loading = ref(false);
const chartData = ref({});

const dynamicsGranularity = computed(() =>
  resolveDynamicsGranularity(props.filters.from, props.filters.to)
);

const formatDynamicsLabels = rows =>
  rows.map(row =>
    formatDynamicsBucketLabel(
      row.date,
      dynamicsGranularity.value,
      t('REPORT.COMPARISON.INCOMPLETE_DAY')
    )
  );

const entityIdParam = computed(() => {
  const id = props.entityId;
  const map = {
    agent: { agentId: id },
    team: { teamId: id },
    label: { labelId: id },
    inbox: { inboxId: id },
  };
  return map[props.type] || {};
});

const entityRow = computed(() => {
  if (props.type === 'label') {
    const label = allLabels.value?.find(
      item => Number(item.id) === Number(props.entityId)
    );
    return { id: props.entityId, name: label?.title };
  }

  return { id: Number(props.entityId) };
});

const archiveContext = computed(() => {
  const labelTitles = (props.filters.labelIds || [])
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);
  const excludeLabelTitles = (props.filters.excludeLabelIds || [])
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);

  return {
    reportType: props.type,
    from: props.filters.from,
    to: props.filters.to,
    entityFilters: {
      agentIds: props.filters.agentIds || [],
      inboxIds: props.filters.inboxIds || [],
      teamIds: props.filters.teamIds || [],
      labelIds: props.filters.labelIds || [],
      labelTitles,
      excludeLabelIds: props.filters.excludeLabelIds || [],
      excludeLabelTitles,
      channelTypes: props.filters.channelTypes || [],
      episodeResult: props.filters.episodeResult || 'all',
      receiptType: props.filters.receiptType || 'all',
      tagPresence: props.filters.tagPresence || 'all',
      resolutionMethod: props.filters.resolutionMethod || 'all',
    },
  };
});

const fetchCharts = async () => {
  if (!props.filters.from || !props.entityId) return;

  loading.value = true;
  try {
    const { data } = await ReportsAPI.getSummaryCharts({
      type: props.type,
      ...entityIdParam.value,
      from: props.filters.from,
      to: props.filters.to,
      businessHours: props.filters.businessHours,
      inboxIds: props.filters.inboxIds,
      teamIds: props.filters.teamIds,
      labelIds: props.filters.labelIds,
      excludeLabelIds: props.filters.excludeLabelIds,
      agentIds: props.filters.agentIds,
      channelTypes: props.filters.channelTypes,
      episodeResult: props.filters.episodeResult,
      receiptType: props.filters.receiptType,
      tagPresence: props.filters.tagPresence,
      resolutionMethod: props.filters.resolutionMethod,
    });
    chartData.value = data;
  } catch {
    chartData.value = {};
  } finally {
    loading.value = false;
  }
};

watch(() => [props.filters, props.entityId, props.type], fetchCharts, {
  deep: true,
  immediate: true,
});

const normalizeOperatorRow = row => ({
  id: row.id,
  name: row.name,
  deleted: row.deleted,
  assignmentEpisodesCount:
    row.assignmentEpisodesCount ?? row.assignment_episodes_count,
  uniqueCyclesHandled: row.uniqueCyclesHandled ?? row.unique_cycles_handled,
  resolvedConversationsCount:
    row.resolvedConversationsCount ?? row.resolved_conversations_count,
  transfersOutCount: row.transfersOutCount ?? row.transfers_out_count,
  medianFirstResponseTime:
    row.medianFirstResponseTime ?? row.median_first_response_time,
  csatAverage: row.csatAverage ?? row.csat_average,
  csatResponses: row.csatResponses ?? row.csat_responses,
});

const operatorsTableRows = computed(() => {
  const rows =
    chartData.value.operators_table || chartData.value.operatorsTable || [];
  return rows.map(normalizeOperatorRow);
});

const frtExtremes = computed(
  () => chartData.value.frt_extremes || chartData.value.frtExtremes || {}
);

const labelInboxDynamicsCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  return {
    labels: formatDynamicsLabels(rows),
    datasets: [
      {
        label: 'Created',
        metricKey: 'conversationsCount',
        data: rows.map(row => row.created),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        tension: 0.3,
      },
      {
        label: 'Resolved',
        metricKey: 'resolutionsCount',
        data: rows.map(row => row.resolved),
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        tension: 0.3,
      },
    ],
  };
});

const agentDynamicsCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  return {
    labels: formatDynamicsLabels(rows),
    datasets: [
      {
        label: 'Accepted',
        metricKey: 'assignmentEpisodesCount',
        data: rows.map(row => row.created),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        tension: 0.3,
      },
      {
        label: 'Resolved',
        metricKey: 'resolutionsCount',
        data: rows.map(row => row.resolved),
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        tension: 0.3,
      },
      {
        label: 'Transferred',
        data: rows.map(row => row.transferred || 0),
        borderColor: '#F59E0B',
        backgroundColor: '#F59E0B',
        tension: 0.3,
      },
    ],
  };
});

const teamDynamicsCollection = computed(() => {
  const rows = chartData.value.daily_dynamics || [];
  return {
    labels: formatDynamicsLabels(rows),
    datasets: [
      {
        label: 'Started',
        metricKey: 'assignmentEpisodesCount',
        data: rows.map(row => row.started),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        tension: 0.3,
      },
      {
        label: 'Resolved',
        metricKey: 'resolutionsCount',
        data: rows.map(row => row.resolved),
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        tension: 0.3,
      },
      {
        label: 'Transferred',
        metricKey: 'transfersOutCount',
        data: rows.map(row => row.transferred || 0),
        borderColor: '#F59E0B',
        backgroundColor: '#F59E0B',
        tension: 0.3,
      },
    ],
  };
});

const dynamicsCollection = computed(() => {
  if (props.type === 'team') return teamDynamicsCollection.value;
  if (props.type === 'agent') return agentDynamicsCollection.value;
  return labelInboxDynamicsCollection.value;
});

const dynamicsRows = computed(() => chartData.value.daily_dynamics || []);

const resolutionCollection = computed(() => {
  const breakdown = chartData.value.resolution_breakdown || {};
  return {
    labels: ['Bot', 'Human'],
    datasets: [
      {
        label: 'Resolved',
        data: [breakdown.bot || 0, breakdown.human || 0],
        backgroundColor: ['#6366F1', '#3B82F6'],
      },
    ],
  };
});

const frtRows = computed(() => {
  const distribution = chartData.value.first_response_distribution;
  if (Array.isArray(distribution)) return distribution;
  return Object.entries(distribution || {}).map(([key, value]) => ({
    key,
    value,
  }));
});

const frtCollection = computed(() => ({
  labels: frtRows.value.map(
    row => t(`CHATS_REPORTS.BUCKETS.FRT.${row.key}`) || row.key
  ),
  datasets: [
    {
      label: t('CHATS_REPORTS.CHARTS.FIRST_RESPONSE'),
      data: frtRows.value.map(row => row.value || 0),
      backgroundColor: '#F59E0B',
    },
  ],
}));

const episodeBreakdownCollection = computed(() => {
  const breakdown = chartData.value.episode_breakdown || {};
  const labels = [
    'Accepted',
    'Answered',
    'Unanswered',
    'Completed',
    'Transferred',
  ];
  return {
    labels,
    datasets: [
      {
        label: 'Episodes',
        data: [
          breakdown.accepted || 0,
          breakdown.answered || 0,
          breakdown.unanswered || 0,
          breakdown.completed || 0,
          breakdown.transferred || 0,
        ],
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#EF4444',
          '#6366F1',
          '#F59E0B',
        ],
      },
    ],
  };
});

const operatorsCollection = computed(() => {
  const rows = chartData.value.operators || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Resolved',
        data: rows.map(row => row.value),
        backgroundColor: '#3B82F6',
      },
    ],
  };
});

const transferInCollection = computed(() => {
  const rows = chartData.value.transfer_breakdown?.in || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Transfers in',
        data: rows.map(row => row.value),
        backgroundColor: '#10B981',
      },
    ],
  };
});

const transferOutCollection = computed(() => {
  const rows = chartData.value.transfer_breakdown?.out || [];
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        label: 'Transfers out',
        data: rows.map(row => row.value),
        backgroundColor: '#F59E0B',
      },
    ],
  };
});

const showDynamics = computed(
  () => (chartData.value.daily_dynamics || []).length > 0
);
const showResolution = computed(() => {
  const breakdown = chartData.value.resolution_breakdown || {};
  return (breakdown.bot || 0) + (breakdown.human || 0) > 0;
});
const showFrt = computed(() =>
  frtRows.value.some(row => Number(row.value) > 0)
);
const showEpisodeBreakdown = computed(() => {
  const breakdown = chartData.value.episode_breakdown || {};
  return Object.values(breakdown).some(value => value > 0);
});
const showOperators = computed(
  () => (chartData.value.operators || []).length > 0
);
const showOperatorsTable = computed(() => operatorsTableRows.value.length > 0);
const showTransferMatrix = computed(
  () =>
    props.type === 'team' &&
    (chartData.value.transfer_matrix || chartData.value.transferMatrix)?.teams
      ?.length
);
const showTransferIn = computed(
  () => (chartData.value.transfer_breakdown?.in || []).length > 0
);
const showTransferOut = computed(
  () => (chartData.value.transfer_breakdown?.out || []).length > 0
);
const showFrtExtremes = computed(
  () =>
    props.type === 'agent' &&
    (frtExtremes.value.min_seconds ||
      frtExtremes.value.minSeconds ||
      frtExtremes.value.max_seconds ||
      frtExtremes.value.maxSeconds)
);

const dynamicsTitleKey = computed(() => {
  const map = {
    agent: 'SUMMARY_REPORTS.CHARTS.AGENT_DYNAMICS',
    team: 'SUMMARY_REPORTS.CHARTS.TEAM_DYNAMICS',
    label: 'SUMMARY_REPORTS.CHARTS.LABEL_DYNAMICS',
    inbox: 'SUMMARY_REPORTS.CHARTS.INBOX_DYNAMICS',
  };
  return map[props.type];
});

const transferMatrixData = computed(
  () => chartData.value.transfer_matrix || chartData.value.transferMatrix
);

const EPISODE_METRIC_KEYS = [
  'assignmentEpisodesCount',
  'answeredEpisodesCount',
  'unansweredEpisodesCount',
  'resolutionsCount',
  'transfersOutCount',
];

const onDynamicsPointClick = event => {
  const dateLabel = dynamicsRows.value[event.index]?.date;
  if (!dateLabel) return;

  const { metricKey } =
    dynamicsCollection.value.datasets[event.datasetIndex] || {};
  if (!metricKey) return;

  const filters = buildDailyArchiveFilters({
    metricKey,
    reportType: props.type,
    row: entityRow.value,
    context: archiveContext.value,
    dateLabel,
  });

  openArchive({
    metricKey,
    reportType: props.type,
    row: entityRow.value,
    context: archiveContext.value,
    filters,
  });
};

const onEpisodeBreakdownClick = event => {
  const metricKey = EPISODE_METRIC_KEYS[event.index];
  if (!metricKey) return;

  openArchive({
    metricKey,
    reportType: 'agent',
    row: entityRow.value,
    context: archiveContext.value,
  });
};

const onOperatorsBarClick = event => {
  const rows = chartData.value.operators || [];
  const row = rows[event.index];
  if (!row?.id) return;

  openArchive({
    metricKey: 'resolutionsCount',
    reportType: 'agent',
    row: { id: row.id, name: row.name },
    context: archiveContext.value,
  });
};

const onResolutionBreakdownClick = event => {
  openArchive({
    metricKey: 'resolutionsCount',
    reportType: props.type,
    row: entityRow.value,
    context: {
      ...archiveContext.value,
      entityFilters: {
        ...archiveContext.value.entityFilters,
        resolutionMethod: event.index === 0 ? 'bot' : 'human',
      },
    },
  });
};

const onFrtDistributionClick = () => {
  if (props.type !== 'agent') return;

  openArchive({
    metricKey: 'assignmentEpisodesCount',
    reportType: 'agent',
    row: entityRow.value,
    context: archiveContext.value,
  });
};

const onTransferInClick = () => {
  openArchive({
    metricKey: 'transfersInCount',
    reportType: 'team',
    row: entityRow.value,
    context: archiveContext.value,
  });
};

const onTransferOutClick = () => {
  openArchive({
    metricKey: 'transfersOutCount',
    reportType: 'team',
    row: entityRow.value,
    context: archiveContext.value,
  });
};

const openFrtExtremeArchive = kind => {
  const extremes = frtExtremes.value;
  const displayId =
    kind === 'min'
      ? extremes.min_conversation_display_id ||
        extremes.minConversationDisplayId
      : extremes.max_conversation_display_id ||
        extremes.maxConversationDisplayId;

  if (!displayId) return;

  const filters = buildConversationArchiveFilters({
    conversationDisplayId: displayId,
    reportType: 'agent',
    row: entityRow.value,
    context: archiveContext.value,
  });

  openArchive({
    reportType: 'agent',
    row: entityRow.value,
    context: archiveContext.value,
    filters,
  });
};

const minFrtSeconds = computed(
  () => frtExtremes.value.min_seconds ?? frtExtremes.value.minSeconds
);
const maxFrtSeconds = computed(
  () => frtExtremes.value.max_seconds ?? frtExtremes.value.maxSeconds
);
</script>

<template>
  <div v-if="loading" class="mt-4 text-sm text-n-slate-11">
    {{ $t('SUMMARY_REPORTS.CHARTS.LOADING') }}
  </div>
  <div v-else class="mt-4 flex flex-col gap-4">
    <section
      v-if="showFrtExtremes"
      class="grid grid-cols-1 gap-4 md:grid-cols-2"
    >
      <button
        v-if="minFrtSeconds"
        type="button"
        class="rounded-xl border border-n-container bg-n-solid-2 px-4 py-4 text-left shadow outline-1 outline outline-n-container hover:border-n-brand"
        @click="openFrtExtremeArchive('min')"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('SUMMARY_REPORTS.FRT_EXTREMES.MIN') }}
        </p>
        <p class="mt-2 text-2xl font-semibold text-n-brand tabular-nums">
          {{ formatTime(minFrtSeconds) }}
        </p>
      </button>
      <button
        v-if="maxFrtSeconds"
        type="button"
        class="rounded-xl border border-n-container bg-n-solid-2 px-4 py-4 text-left shadow outline-1 outline outline-n-container hover:border-n-brand"
        @click="openFrtExtremeArchive('max')"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('SUMMARY_REPORTS.FRT_EXTREMES.MAX') }}
        </p>
        <p class="mt-2 text-2xl font-semibold text-n-brand tabular-nums">
          {{ formatTime(maxFrtSeconds) }}
        </p>
      </button>
    </section>

    <section
      v-if="showDynamics"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t(dynamicsTitleKey) }}
      </h3>
      <div class="mt-4 h-72">
        <LineChart
          :collection="dynamicsCollection"
          @point-click="onDynamicsPointClick"
        />
      </div>
    </section>

    <SummaryTeamOperatorsTable
      v-if="type === 'team' && showOperatorsTable"
      :rows="operatorsTableRows"
      :archive-context="archiveContext"
    />

    <TeamTransferMatrix
      v-if="showTransferMatrix"
      :matrix-data="transferMatrixData"
      :archive-context="archiveContext"
    />

    <section
      v-if="type === 'agent' && showEpisodeBreakdown"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.EPISODE_BREAKDOWN') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="episodeBreakdownCollection"
          @bar-click="onEpisodeBreakdownClick"
        />
      </div>
    </section>

    <section
      v-if="type === 'team' && showOperators"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('SUMMARY_REPORTS.CHARTS.TEAM_OPERATORS') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="operatorsCollection"
          :show-legend="false"
          @bar-click="onOperatorsBarClick"
        />
      </div>
    </section>

    <div
      v-if="type === 'team' && (showTransferIn || showTransferOut)"
      class="grid grid-cols-1 gap-4 xl:grid-cols-2"
    >
      <section
        v-if="showTransferIn"
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.CHARTS.TRANSFERS_IN') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="transferInCollection"
            :show-legend="false"
            @bar-click="onTransferInClick"
          />
        </div>
      </section>

      <section
        v-if="showTransferOut"
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.CHARTS.TRANSFERS_OUT') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="transferOutCollection"
            :show-legend="false"
            @bar-click="onTransferOutClick"
          />
        </div>
      </section>
    </div>

    <div
      v-if="['label', 'inbox'].includes(type) && (showResolution || showFrt)"
      class="grid grid-cols-1 gap-4 xl:grid-cols-2"
    >
      <section
        v-if="showResolution"
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.CHARTS.RESOLUTION_BREAKDOWN') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="resolutionCollection"
            @bar-click="onResolutionBreakdownClick"
          />
        </div>
      </section>

      <section
        v-if="showFrt"
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.CHARTS.FIRST_RESPONSE_DISTRIBUTION') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="frtCollection"
            @bar-click="onFrtDistributionClick"
          />
        </div>
      </section>
    </div>

    <div
      v-if="type === 'agent' && showFrt"
      class="grid grid-cols-1 gap-4 xl:grid-cols-2"
    >
      <section
        class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.CHARTS.FIRST_RESPONSE_DISTRIBUTION') }}
        </h3>
        <div class="mt-4 h-72">
          <BarChart
            :collection="frtCollection"
            @bar-click="onFrtDistributionClick"
          />
        </div>
      </section>
    </div>
  </div>
</template>
